import constants from '../../common/constants/CommonConstants';

const { ORGANIZATION_LEVEL, ACCOUNT_LEVEL, CONTACT_LEVEL } = constants;

export const HEALTH_APPLICABLE_LEVELS = [
  ORGANIZATION_LEVEL,
  ACCOUNT_LEVEL,
  CONTACT_LEVEL,
];

export const HEALTH_SCOPES = {
  GLOBAL: 'GLOBAL',
  LIFECYCLE: 'LIFECYCLE',
  // SEGMENT: 'SEGMENT',
};

export const HEALTH_SCOPES_IDS = {
  [HEALTH_SCOPES.GLOBAL]: 1,
  [HEALTH_SCOPES.LIFECYCLE]: 2,
};

export const RAG_STATUSES = {
  RED: 'Red',
  AMBER: 'Amber',
  GREEN: 'Green',
};

export const HEALTH_STATUS = [
  {
    value: RAG_STATUSES.RED,
    displayName: 'Poor',
  },
  {
    value: RAG_STATUSES.AMBER,
    displayName: 'Average',
  },
  {
    value: RAG_STATUSES.GREEN,
    displayName: 'Good',
  },
];

export const RAG_STATUS_HEALTH_STATUS_MAP = {
  [RAG_STATUSES.RED]: 'Poor',
  [RAG_STATUSES.AMBER]: 'Average',
  [RAG_STATUSES.GREEN]: 'Good',
};

export const RAG_STATUS_COLOR_CLASS_MAP = {
  None: '',
  [RAG_STATUSES.RED]: 'bad',
  [RAG_STATUSES.AMBER]: 'warn',
  [RAG_STATUSES.GREEN]: 'good',
};

export const HEALTH_DATA_TYPES = {
  // basic
  boolean: 'boolean', // checkbox
  number: 'number',
  date: 'date',
  // custom
  currency: 'currency',
  singlePicklist: 'singlePicklist',
};

export const HEALTH_CRITERIA_OPERATORS = {
  isEqualTo: 'eq',
  isOneOf: 'oneOf',
  lessThan: 'lt',
  lessThanOrEqualTo: 'lte',
  greaterThan: 'gt',
  greaterThanOrEqualTo: 'gte',
};

export const HEALTH_SYSTEM_INDICATORS = {
  lastTouch: {
    name: 'lastTouch',
    displayName: 'Last Touch',
    dataType: HEALTH_DATA_TYPES.date,
    level: HEALTH_APPLICABLE_LEVELS,
  },
  csmPulse: {
    name: 'csmPulse',
    displayName: 'CSM Pulse',
    dataType: HEALTH_DATA_TYPES.number,
    level: HEALTH_APPLICABLE_LEVELS,
  },
  linkedContactsCSMPulse: {
    name: 'linkedContactsCSMPulse',
    displayName: 'Linked Contacts CSM Pulse',
    dataType: HEALTH_DATA_TYPES.number,
    level: [ORGANIZATION_LEVEL, ACCOUNT_LEVEL],
  },
  linkedAccountsCSMPulse: {
    name: 'linkedAccountsCSMPulse',
    displayName: 'Linked Accounts CSM Pulse',
    dataType: HEALTH_DATA_TYPES.number,
    level: [ORGANIZATION_LEVEL],
  },
  linkedAccountsHealthScore: {
    name: 'linkedAccountsHealthScore',
    displayName: 'Linked Accounts Health Score',
    dataType: HEALTH_DATA_TYPES.number,
    level: [ORGANIZATION_LEVEL],
  },
  linkedContactsHealthScore: {
    name: 'linkedContactsHealthScore',
    displayName: 'Linked Contacts Health Score',
    dataType: HEALTH_DATA_TYPES.number,
    level: [ORGANIZATION_LEVEL, ACCOUNT_LEVEL],
  },
  openOpportunitiesExist: {
    name: 'openOpportunitiesExist',
    displayName: 'Open Opportunity',
    dataType: HEALTH_DATA_TYPES.boolean,
    level: [ORGANIZATION_LEVEL, ACCOUNT_LEVEL],
  },
  openRisksExist: {
    name: 'openRisksExist',
    displayName: 'Open Risk',
    dataType: HEALTH_DATA_TYPES.boolean,
    level: [ORGANIZATION_LEVEL, ACCOUNT_LEVEL],
  },
  numberOfDaysInLifecycleStage: {
    name: 'numberOfDaysInLifecycleStage',
    displayName: 'Number of Days in Lifecycle Stage',
    dataType: HEALTH_DATA_TYPES.number,
    level: HEALTH_APPLICABLE_LEVELS,
  },
  ticketsCount: {
    name: 'ticketsCount',
    displayName: 'Number of Tickets',
    dataType: HEALTH_DATA_TYPES.number,
    level: [ORGANIZATION_LEVEL, ACCOUNT_LEVEL],
  },
  stagnatedTicketsCount: {
    name: 'stagnatedTicketsCount',
    displayName: 'Tickets Stagnated',
    dataType: HEALTH_DATA_TYPES.number,
    level: [ORGANIZATION_LEVEL, ACCOUNT_LEVEL],
  },
  nps: {
    name: 'nps',
    displayName: 'NPS',
    dataType: HEALTH_DATA_TYPES.number,
    level: [ORGANIZATION_LEVEL, ACCOUNT_LEVEL],
  },
  lastNpsRating: {
    name: 'lastNpsRating',
    displayName: 'Last NPS Rating',
    dataType: HEALTH_DATA_TYPES.number,
    level: [CONTACT_LEVEL],
  },
};

export const HELPDESK_TICKET_INDICATORS = [
  HEALTH_SYSTEM_INDICATORS.ticketsCount.name,
  HEALTH_SYSTEM_INDICATORS.stagnatedTicketsCount.name,
];

export const DATE_INDICATOR_GRANULARITY_OPTIONS = {
  day: 'day',
  week: 'week',
  month: 'month',
  quarter: 'quarter',
};

export const COMPARISON_GRANULARITY_OPTIONS = {
  day: {
    value: 'day',
    displayName: 'Days ago',
  },
  week: {
    value: 'week',
    displayName: 'Weeks ago',
  },
  month: {
    value: 'month',
    displayName: 'Months ago',
  },
  quarter: {
    value: 'quarter',
    displayName: 'Quarters ago',
  },
};

export const AGGREGATION_TYPES = {
  sum: {
    value: 'sum',
    displayName: 'Sum',
  },
};

export const AGGREGATION_TIMEFRAME_OPTIONS = {
  last7: {
    value: 'last7',
    displayName: 'Last 7 days',
  },
  last30: {
    value: 'last30',
    displayName: 'Last 30 days',
  },
  last90: {
    value: 'last90',
    displayName: 'Last 90 days',
  },
  calendarWeek: {
    value: 'calendarWeek',
    displayName: 'Calendar Week',
  },
  calendarMonth: {
    value: 'calendarMonth',
    displayName: 'Calendar Month',
  },
  calendarQuarter: {
    value: 'calendarQuarter',
    displayName: 'Calendar Quarter',
  },
};

export const TIMEFRAME_COMPARISON_TYPES = {
  value: {
    name: 'value',
    displayName: 'Value',
  },
  valueIncrease: {
    name: 'valueIncrease',
    displayName: 'Value Increase',
  },
  valueDecrease: {
    name: 'valueDecrease',
    displayName: 'Value Decrease',
  },
  percentageIncrease: {
    name: 'percentageIncrease',
    displayName: 'Percentage Increase',
  },
  percentageDecrease: {
    name: 'percentageDecrease',
    displayName: 'Percentage Decrease',
  },
};

export const DATA_TYPE_COMPARISON_TYPE_MAP = {
  [HEALTH_DATA_TYPES.date]: [TIMEFRAME_COMPARISON_TYPES.value],
  [HEALTH_DATA_TYPES.number]: [
    TIMEFRAME_COMPARISON_TYPES.value,
    TIMEFRAME_COMPARISON_TYPES.valueIncrease,
    TIMEFRAME_COMPARISON_TYPES.valueDecrease,
    TIMEFRAME_COMPARISON_TYPES.percentageIncrease,
    TIMEFRAME_COMPARISON_TYPES.percentageDecrease,
  ],
  [HEALTH_DATA_TYPES.currency]: [
    TIMEFRAME_COMPARISON_TYPES.value,
    TIMEFRAME_COMPARISON_TYPES.valueIncrease,
    TIMEFRAME_COMPARISON_TYPES.valueDecrease,
    TIMEFRAME_COMPARISON_TYPES.percentageIncrease,
    TIMEFRAME_COMPARISON_TYPES.percentageDecrease,
  ],
  [HEALTH_DATA_TYPES.boolean]: [TIMEFRAME_COMPARISON_TYPES.value],
  [HEALTH_DATA_TYPES.singlePicklist]: [TIMEFRAME_COMPARISON_TYPES.value],
};

export const MAX_CACHING_TIME_FOR_HEALTH_BREAKDOWN_IN_SEC = 1;
